import React, { useCallback, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Config from '../Config';
import {
  getSummary, getTrend, getBreakdown, getCustomers, getCustomerDetail, downloadExport,
} from '../../services/CartAnalyticsService';
import {
  defaultRange, parseApiDate, toApiDate, rangeError,
} from './cartAnalyticsFormat';
import KpiCards from './KpiCards';
import TrendChart from './TrendChart';
import BreakdownTabs from './BreakdownTabs';
import CustomerTable from './CustomerTable';
import CustomerDetail from './CustomerDetail';
import './CartAnalytics.scss';

const PERIODS = [['day', 'Day'], ['week', 'Week'], ['month', 'Month'], ['year', 'Year']];
const STATUSES = [['ALL', 'All carts'], ['INCOMPLETE', 'Incomplete'], ['COMPLETED', 'Completed'], ['SAVE_LATER', 'Saved for later']];

const messageFor = (err, fallback) => {
  const body = err && err.response && err.response.data;
  if (body && body.error_message && err.response.status !== 500) return body.error_message;
  if (err && err.response && [400, 401, 402, 403].includes(err.response.status)) {
    return 'You do not have access to Cart Analytics (or the menu is not set up).';
  }
  return fallback;
};

export default function CartAnalytics() {
  const initial = defaultRange(new Date());
  const [from, setFrom] = useState(parseApiDate(initial.from));
  const [to, setTo] = useState(parseApiDate(initial.to));
  const [period, setPeriod] = useState('day');
  const [currency, setCurrency] = useState('');
  const [status, setStatus] = useState('ALL');
  const [includeSamples, setIncludeSamples] = useState(true);
  const [filterError, setFilterError] = useState('');

  const [applied, setApplied] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [piiVisible, setPiiVisible] = useState(false);

  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const [by, setBy] = useState('country');
  const [breakdown, setBreakdown] = useState([]);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [customers, setCustomers] = useState([]);
  const [customerSummary, setCustomerSummary] = useState(null);
  const [sort, setSort] = useState('last_activity');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [custSearch, setCustSearch] = useState('');

  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [exporting, setExporting] = useState(false);

  const reqOverview = useRef(0);
  const reqBreakdown = useRef(0);
  const reqCustomers = useRef(0);

  const baseParams = (f) => ({
    from: f.from, to: f.to, period: f.period, currency: f.currency, status: f.status, include_samples: f.includeSamples ? 1 : 0,
  });

  const loadOverview = useCallback(async (f) => {
    const id = ++reqOverview.current;
    setOverviewLoading(true);
    try {
      const [s, t] = await Promise.all([getSummary(baseParams(f)), getTrend(baseParams(f))]);
      if (id !== reqOverview.current) return;
      const sb = s.data || {};
      const cur = (sb.result && sb.result.current) || [];
      const prev = (sb.result && sb.result.previous) || [];
      setSummary({ current: cur, previous: prev });
      setPiiVisible(!!sb.pii_visible);
      setCurrencies((old) => Array.from(new Set([...old, ...cur.map((r) => r.ccy).filter(Boolean)])));
      setTrend((t.data && t.data.result) || []);
    } catch (err) {
      if (id === reqOverview.current) Config.createNotification('error', messageFor(err, 'Failed to load KPIs'));
    } finally {
      if (id === reqOverview.current) setOverviewLoading(false);
    }
  }, []);

  const loadBreakdown = useCallback(async (f, dimension, text = '') => {
    const id = ++reqBreakdown.current;
    setBreakdownLoading(true);
    try {
      const res = await getBreakdown({ ...baseParams(f), by: dimension, q: text });
      if (id !== reqBreakdown.current) return;
      setBreakdown((res.data && res.data.result) || []);
    } catch (err) {
      if (id === reqBreakdown.current) Config.createNotification('error', messageFor(err, 'Failed to load breakdown'));
    } finally {
      if (id === reqBreakdown.current) setBreakdownLoading(false);
    }
  }, []);

  const loadCustomers = useCallback(async (f, sortKey, sortOrder, pageNo, text = '') => {
    const id = ++reqCustomers.current;
    setCustomersLoading(true);
    try {
      const res = await getCustomers({
        ...baseParams(f), sort: sortKey, order: sortOrder, page: pageNo, limit: 25, q: text,
      });
      if (id !== reqCustomers.current) return;
      const body = res.data || {};
      setCustomers(body.result || []);
      setCustomerSummary(body.summary || null);
      setPiiVisible(!!body.pii_visible);
      setPage(pageNo);
    } catch (err) {
      if (id === reqCustomers.current) Config.createNotification('error', messageFor(err, 'Failed to load customers'));
    } finally {
      if (id === reqCustomers.current) setCustomersLoading(false);
    }
  }, []);

  const apply = useCallback(() => {
    const f = {
      from: toApiDate(from), to: toApiDate(to), period, currency, status, includeSamples,
    };
    const err = rangeError(f.from, f.to, f.period);
    setFilterError(err || '');
    if (err) return;
    setApplied(f);
    loadOverview(f);
    loadBreakdown(f, by, search);
    loadCustomers(f, sort, order, 1, custSearch);
  }, [from, to, period, currency, status, includeSamples, by, search, custSearch, sort, order, loadOverview, loadBreakdown, loadCustomers]);

  useEffect(() => {
    apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTab = (dimension) => {
    setBy(dimension);
    setSearch('');
    if (applied) loadBreakdown(applied, dimension, '');
  };

  const onSearch = (text) => {
    setSearch(text);
    if (applied) loadBreakdown(applied, by, text);
  };

  const onSort = (field) => {
    const nextOrder = sort === field && order === 'desc' ? 'asc' : 'desc';
    setSort(field);
    setOrder(nextOrder);
    if (applied) loadCustomers(applied, field, nextOrder, 1, custSearch);
  };

  const onPage = (pageNo) => {
    if (applied) loadCustomers(applied, sort, order, pageNo, custSearch);
  };

  const onCustomerSearch = (text) => {
    setCustSearch(text);
    if (applied) loadCustomers(applied, sort, order, 1, text);
  };

  const openDetail = async (row) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const res = await getCustomerDetail(row.customer_key);
      setDetail((res.data && res.data.result) || null);
    } catch (err) {
      setDetailError(messageFor(err, 'Failed to load details'));
    } finally {
      setDetailLoading(false);
    }
  };

  const doExport = async () => {
    if (!applied) return;
    setExporting(true);
    try {
      await downloadExport({ ...baseParams(applied), sort, order });
    } catch (err) {
      const status403 = err && err.response && err.response.status === 403;
      Config.createNotification('error', status403 ? 'You are not allowed to export this report' : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const displayCcy = (applied && applied.currency) || (summary && summary.current[0] && summary.current[0].ccy) || '';
  const current = summary && summary.current.find((r) => r.ccy === displayCcy);
  const previous = summary && summary.previous.find((r) => r.ccy === displayCcy);
  const rows = customers.filter((r) => !displayCcy || r.ccy === displayCcy);

  return (
    <div className="ca-page">
      <div className="d-flex align-items-start justify-content-between mb-3">
        <div>
          <h1 className="ca-title mb-1">Cart Analytics</h1>
          <p className="text-muted mb-0" style={{ fontSize: 13 }}>Who is adding to the cart, what they add, and where they come from. Figures cover items still in the cart or ordered.</p>
        </div>
        <button type="button" className="btn btn-sm btn-outline-primary ml-3 flex-shrink-0" onClick={doExport} disabled={exporting || !applied}>
          {exporting ? 'Preparing Excel…' : 'Export Excel'}
        </button>
      </div>

      <div className="ca-filters-card">
        <form onSubmit={(e) => { e.preventDefault(); apply(); }}>
          <div className="ca-filter-row">
            <div className="ca-filter-field ca-f-date">
              <span className="ca-filter-label">From</span>
              <DatePicker selected={from} onChange={setFrom} className="form-control form-control-sm" dateFormat="dd-MMM-yyyy" maxDate={new Date()} />
            </div>
            <div className="ca-filter-field ca-f-date">
              <span className="ca-filter-label">To</span>
              <DatePicker selected={to} onChange={setTo} className="form-control form-control-sm" dateFormat="dd-MMM-yyyy" maxDate={new Date()} />
            </div>
            <div className="ca-filter-field ca-f-period">
              <span className="ca-filter-label">View by</span>
              <div className="ca-period-group">
                {PERIODS.map(([k, l]) => (
                  <button key={k} type="button" className={`btn btn-sm ${period === k ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod(k)}>{l}</button>
                ))}
              </div>
            </div>
            <div className="ca-filter-field ca-f-status">
              <span className="ca-filter-label">Cart status</span>
              <select className="form-control form-control-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div className="ca-filter-field ca-f-ccy">
              <span className="ca-filter-label">Currency</span>
              <select className="form-control form-control-sm" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                <option value="">All</option>
                {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="ca-filter-field ca-f-samples">
              <span className="ca-filter-label">Samples</span>
              <label className="ca-samples-check mb-0">
                <input type="checkbox" checked={includeSamples} onChange={(e) => setIncludeSamples(e.target.checked)} />
                Include
              </label>
            </div>
            <div className="ca-filter-field ca-f-apply">
              <span className="ca-filter-label">&nbsp;</span>
              <button type="submit" className="btn btn-primary btn-sm btn-block" disabled={overviewLoading}>Apply</button>
            </div>
          </div>
          {filterError && <div className="alert alert-warning py-1 mb-0 mt-2">{filterError}</div>}
        </form>
      </div>

      {overviewLoading && <div className="ca-loading mb-3">Loading…</div>}
      {summary && summary.current.length === 0 && !overviewLoading && (
        <div className="alert alert-light border">No cart activity found for this range.</div>
      )}
      {summary && summary.current.length > 1 && !applied.currency && (
        <div className="alert alert-info py-1">Several currencies found. Showing {displayCcy}; pick a currency above to see another. Amounts are never added across currencies.</div>
      )}
      <KpiCards current={current} previous={previous} trend={trend} ccy={displayCcy} />
      <TrendChart rows={trend} ccy={displayCcy} period={applied ? applied.period : period} />
      <BreakdownTabs by={by} onChange={onTab} rows={breakdown} loading={breakdownLoading} ccy={displayCcy} search={search} onSearch={onSearch} />
      <CustomerTable
        rows={rows}
        summary={customerSummary}
        piiVisible={piiVisible}
        sort={sort}
        order={order}
        onSort={onSort}
        page={page}
        onPage={onPage}
        loading={customersLoading}
        onSelect={openDetail}
        search={custSearch}
        onSearch={onCustomerSearch}
      />
      {detailOpen && (
        <CustomerDetail
          data={detail}
          loading={detailLoading}
          error={detailError}
          piiVisible={piiVisible}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
}
